import { useCallback, useEffect, useRef, useState } from 'react';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

const FACE_CHECK_INTERVAL = 500;

export default function useFaceDetector({ webcamRef, cameraError }) {
  const [faceStatus, setFaceStatus] = useState('detecting'); 
  const detectorRef = useRef(null);
  const timerRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks('/mediapipe/wasm');
        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite',
          },
          runningMode: 'VIDEO',
          minDetectionConfidence: 0.5,
        });
        if (!cancelled) detectorRef.current = detector;
      } catch (err) {
        console.error('Face detector init error:', err);
      }
    })();
    return () => {
      cancelled = true;
      try { detectorRef.current?.close?.(); } catch (e) {}
      detectorRef.current = null;
    };
  }, []);

  const checkFace = useCallback(() => {
    const detector = detectorRef.current;
    const video = webcamRef.current?.video;
    if (!detector || !video) return;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

    const stream = video.srcObject;
    const track = stream?.getVideoTracks?.()[0];
    if (track && track.readyState !== 'live') { setFaceStatus('face_missing'); return; }
    if (video.videoWidth <= 0 || video.videoHeight <= 0) { setFaceStatus('face_missing'); return; }
    if (video.currentTime === lastVideoTimeRef.current) return;
    lastVideoTimeRef.current = video.currentTime;

    try {
      const result = detector.detectForVideo(video, performance.now());
      const count = result?.detections?.length || 0;
      setFaceStatus(count === 0 ? 'face_missing' : count === 1 ? 'ok' : 'face_multiple');
    } catch (err) {
      console.error('Face detection error:', err);
      setFaceStatus('face_missing');
    }
  }, [webcamRef]);

  useEffect(() => {
    if (cameraError) return undefined;
    timerRef.current = setInterval(checkFace, FACE_CHECK_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [cameraError, checkFace]);

  return { faceStatus, setFaceStatus };
}